import React, { useState } from 'react'
import {
  Cell,
  DataBlock,
  Divider,
  Expander,
  ExpanderContent,
  ExpanderHeader,
  Grid,
  LabelTooltip,
  ResponsiveTableRow,
  H5,
} from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import ClickToCopy from '@fs/zion-click-to-copy'
import { Link } from '@fs/zion-router'
import { DmcResponsiveTable } from '../../../common/DmcResponsiveTable'
import { getFrontendBaseUrl } from '../../../../util/UrlUtil'

export default function OdmRecordOrdinances({ ordinances }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(true)

  const extractValues = (ordinance) => {
    const dashes = '--'
    return {
      status: ordinance?.status?.value || dashes,
      ordDate: ordinance?.date?.value || dashes,
      templeName: ordinance?.temple?.value || dashes,
      ordPlace: ordinance?.place?.value || dashes,
      ordClass: ordinance?.customTags?.[1]?.value || dashes,
      role: ordinance?.customTags?.[2]?.value || dashes,
      temple: ordinance?.customTags?.[3]?.value || dashes,
      barcode: ordinance?.customTags?.[4]?.value || dashes,
      releaseDate: ordinance?.customTags?.[5]?.value || dashes,
      deleteDate: ordinance?.customTags?.[6]?.value || dashes,
      processed: ordinance?.customTags?.[7]?.value || dashes,
      filmNumber: ordinance?.customTags?.[8]?.value || dashes,
      bookNumber: ordinance?.customTags?.[9]?.value || dashes,
      page: ordinance?.customTags?.[10]?.value || dashes,
      type: ordinance?.customTags?.[11]?.value || dashes,
      transmissionNumber: ordinance?.customTags?.[12]?.value || dashes,
      ordNumber: ordinance?.customTags?.[14]?.value || dashes,
      livingAtOrd: ordinance?.customTags?.[15]?.value || dashes,
    }
  }

  return (
    <>
      <Divider marginY="nano" />
      <Expander open={open} setOpen={setOpen}>
        <ExpanderHeader>
          <H5>{t('ordinances.label', 'Ordinances')}</H5>
        </ExpanderHeader>
        <ExpanderContent>
          {ordinances && (
            <DmcResponsiveTable
              atSizeColumns={{ default: { labels: ['', ''], columnWidths: [1, 11] } }}
              totalColumnCount={2}
            >
              {ordinances.map((rawOrd) => {
                const ordinance = extractValues(rawOrd)
                return (
                  <ResponsiveTableRow
                    key={`${ordinance.type}-${ordinance.temple}-${ordinance.ordDate}`}
                    column1={<DataBlock heading={t('type', 'Type')} data={ordinance.type} />}
                    column2={
                      <Grid guttersY="nano">
                        <Cell columns={1}>
                          <DataBlock heading={t('odm.ordinance.date', 'Ord Date')} data={ordinance.ordDate} />
                        </Cell>
                        <Cell columns={1}>
                          <DataBlock heading={t('temple.label', 'Temple')} data={ordinance.temple} />
                        </Cell>
                        <Cell columns={1}>
                          <DataBlock heading={t('odm.ordinance.class', 'Class')} data={ordinance.ordClass} />
                        </Cell>
                        <Cell columns={1}>
                          <DataBlock heading={t('odm.event.role', 'Role')} data={ordinance.role} />
                        </Cell>
                        <Cell columns={2}>
                          <DataBlock
                            heading={t('odm.ordinance.barcode', 'Barcode')}
                            data={
                              ordinance.barcode === '--' ? (
                                ordinance.barcode
                              ) : (
                                <Link
                                  to={`${getFrontendBaseUrl()}/temple/ows/${ordinance.barcode}/summary`}
                                  target="_blank"
                                  noScrollTop
                                >
                                  {ordinance.barcode}
                                </Link>
                              )
                            }
                          />
                        </Cell>
                        <Cell columns={1}>
                          <DataBlock heading={t('status.label', 'Status')} data={ordinance.status} />
                        </Cell>
                        <Cell columns={2}>
                          <DataBlock
                            heading={t('odm.ordinance.date.release', 'Release Date')}
                            data={ordinance.releaseDate}
                          />
                        </Cell>
                        <Cell columns={1}>
                          <DataBlock
                            heading={t('odm.ordinance.date.delete', 'Delete Date')}
                            data={ordinance.deleteDate}
                          />
                        </Cell>
                        <Cell columns={2}>
                          <DataBlock
                            heading={t('odm.ordinance.processing', 'Processing By')}
                            data={ordinance.processed}
                          />
                        </Cell>
                        <Cell columns={1}>
                          <DataBlock
                            heading={t('odm.ordinance.film', 'Film #')}
                            data={
                              ordinance.filmNumber === '--' ? (
                                ordinance.filmNumber
                              ) : (
                                <LabelTooltip
                                  target={<ClickToCopy value={ordinance.filmNumber} />}
                                  label={ordinance.filmNumber}
                                />
                              )
                            }
                          />
                        </Cell>
                        <Cell columns={1}>
                          <DataBlock heading={t('odm.ordinance.book', 'Book #')} data={ordinance.bookNumber} />
                        </Cell>
                        <Cell columns={1}>
                          <DataBlock heading={t('odm.ordinance.page', 'Page')} data={ordinance.page} />
                        </Cell>
                        <Cell columns={1}>
                          <DataBlock heading={t('odm.ordinance.number', 'Ordinance #')} data={ordinance.ordNumber} />
                        </Cell>
                        <Cell columns={2}>
                          <DataBlock
                            heading={t('odm.ordinance.transmission', 'Transmission #')}
                            data={ordinance.transmissionNumber}
                          />
                        </Cell>
                        <Cell columns={1}>
                          <DataBlock heading={t('odm.temple.name', 'Temple Name')} data={ordinance.templeName} />
                        </Cell>
                        <Cell columns={2}>
                          <DataBlock heading={t('odm.ordinance.place', 'Ordinance Place')} data={ordinance.ordPlace} />
                        </Cell>
                        <Cell columns={1}>
                          <DataBlock
                            heading={t('odm.ordinance.living', 'Living At Ordinance')}
                            data={ordinance.livingAtOrd}
                          />
                        </Cell>
                      </Grid>
                    }
                  />
                )
              })}
            </DmcResponsiveTable>
          )}
          {(!ordinances || ordinances.length <= 0) && <p>{t('odm.noOrdinances', 'No Ordinances')}</p>}
          <Divider marginY="nano" />
        </ExpanderContent>
      </Expander>
    </>
  )
}
